import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface InterviewFolder {
  id: string;
  name: string;
  created_at: string;
}

export interface FolderAssignment {
  interview_id: string;
  folder_id: string;
}

export const useInterviewFolders = () => {
  const queryClient = useQueryClient();

  const { data: folders = [], isLoading: foldersLoading } = useQuery<InterviewFolder[]>({
    queryKey: ['interviewFolders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_folders')
        .select('id, name, created_at')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: assignments = [] } = useQuery<FolderAssignment[]>({
    queryKey: ['folderAssignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_folder_assignments')
        .select('interview_id, folder_id');
      if (error) throw error;
      return data || [];
    },
  });

  const createFolder = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from('interview_folders')
        .insert({ name })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewFolders'] });
      toast.success('Mappe opprettet');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke opprette mappe: ${error.message}`);
    },
  });

  const renameFolder = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from('interview_folders')
        .update({ name })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewFolders'] });
      toast.success('Mappe omdøpt');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke omdøpe mappe: ${error.message}`);
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('interview_folders')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewFolders'] });
      queryClient.invalidateQueries({ queryKey: ['folderAssignments'] });
      toast.success('Mappe slettet');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke slette mappe: ${error.message}`);
    },
  });

  const assignToFolder = useMutation({
    mutationFn: async ({ interviewIds, folderId }: { interviewIds: string[]; folderId: string }) => {
      const rows = interviewIds.map(interview_id => ({ interview_id, folder_id: folderId }));
      const { error } = await supabase
        .from('interview_folder_assignments')
        .upsert(rows, { onConflict: 'interview_id,folder_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderAssignments'] });
      toast.success('Artikler lagt til i mappe');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke flytte artikler: ${error.message}`);
    },
  });

  const removeFromFolder = useMutation({
    mutationFn: async ({ interviewIds, folderId }: { interviewIds: string[]; folderId: string }) => {
      const { error } = await supabase
        .from('interview_folder_assignments')
        .delete()
        .eq('folder_id', folderId)
        .in('interview_id', interviewIds);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderAssignments'] });
      toast.success('Artikler fjernet fra mappe');
    },
    onError: (error: Error) => {
      toast.error(`Kunne ikke fjerne artikler: ${error.message}`);
    },
  });

  const getInterviewIdsForFolder = (folderId: string): string[] => {
    return assignments.filter(a => a.folder_id === folderId).map(a => a.interview_id);
  };

  const getFolderIdsForInterview = (interviewId: string): string[] => {
    return assignments.filter(a => a.interview_id === interviewId).map(a => a.folder_id);
  };

  return {
    folders,
    foldersLoading,
    assignments,
    createFolder,
    renameFolder,
    deleteFolder,
    assignToFolder,
    removeFromFolder,
    getInterviewIdsForFolder,
    getFolderIdsForInterview,
  };
};
